import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal, Form, Row, Col, Input, DatePicker, notification, Card, Progress, Divider } from "antd";
import { EyeOutlined, SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import AllPartnersForOrdersSelect from "../Partners/AllPartnersForOrdersSelect";
import AvailableLocationsSelect from "../Partners/AvailableLocationsSelect";
import PartnerStateSelector from "../Partners/PartnerStateSelector";
import PartnerLink from "../Partners/PartnerLink";
import PartnerFilterTable from "../Partners/PartnerFilterTable";
import moment from "moment";
import { coreApi } from "../../api/calls";
import PdfSettingsForm from "../PdfSettingsForm";
import { clearAvailablePartnerStates } from "../../actions";
import usePartnerFiltering from "../../hooks/usePartnerFiltering";
import { getCurrentUserEmployeeId } from "./managerNameOverride";
import ManagerSelect from "../ManagerSelect";

const OfferVacantHousesModal = ({
  isVisible,
  onCancel,
  selectedUnits,
  getTheActiveDays,
  handleApiError
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [offerVacantForm] = Form.useForm();
  const defaultManagerEmployeeId = getCurrentUserEmployeeId();

  const {
    selectedPartners,
    filteredPartners,
    checkedPartners,
    handlePartnersLoaded,
    handlePartnerSelectionChange,
    handlePartnerCheck,
    getSelectedPartnerIds,
    selectAll,
    deselectAll,
    resetFiltering,
  } = usePartnerFiltering();

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [emailPreview, setEmailPreview] = useState("");
  const [additionalPDF, setAdditionalPDF] = useState("downloadPdf");
  const [productType, setProductType] = useState("automatic");
  const [displayPrice, setDisplayPrice] = useState("with_substitute_reward");
  const [selectedLanguage, setSelectedLanguage] = useState("automatic");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [hideStreetNumber, setHideStreetNumber] = useState(true);
  const [selectedPartnerStates, setSelectedPartnerStates] = useState([]);

  useEffect(() => {
    if (!isVisible) return;

    const cityNames = [...new Set(
      (selectedUnits || [])
        .map((unit) => unit?.city_branch?.name || unit?.city_branch?.name_eng)
        .filter(Boolean)
    )].join(", ");

    const countryCode = (
      selectedUnits?.[0]?.country_branch?.country_code_iso_3166 ||
      selectedUnits?.[0]?.city_branch?.country_code_iso_3166 ||
      "CZ"
    ).toUpperCase();

    const cityNameSegment = cityNames ? ` ${cityNames}` : "";
    const subjectTemplates = {
      SK: t("emails.offerSubjectTemplates.offerVacantHousesForSubstitute.sk", { cityNameSegment }),
      CZ: t("emails.offerSubjectTemplates.offerVacantHousesForSubstitute.cz", { cityNameSegment }),
      default: t("emails.offerSubjectTemplates.offerVacantHousesForSubstitute.default", { cityNameSegment }),
    };
    const defaultSubject = subjectTemplates[countryCode] || subjectTemplates.default;

    offerVacantForm.setFieldsValue({
      subject: defaultSubject,
      manager_employee_id: defaultManagerEmployeeId,
    });
  }, [defaultManagerEmployeeId, isVisible, selectedUnits, offerVacantForm, t]);

  const handlePartnersLoadedWithForm = useCallback((partners) => {
    const partnerIds = handlePartnersLoaded(partners);
    if (partnerIds.length > 0) {
      offerVacantForm.setFieldValue("partners", partnerIds);
    }
  }, [handlePartnersLoaded, offerVacantForm]);

  const buildPayload = (values) => {
    const building_units = selectedUnits.map((unit) => {
      const cleaningRange = values?.cleaning_range?.[unit.id] || [];
      const lastCleaning = cleaningRange[0];
      const firstCleaning = cleaningRange[1];
      return {
        id: unit.id,
        building_unit_id: unit.building_unit?.id || unit.building_unit_id,
        city_branch: unit.city_branch,
        address: `${unit.building_unit?.street || ""} ${unit.building_unit?.house_number || ""}`,
        last_cleaning_days: getTheActiveDays(unit, t),
        new_cleaning_days: getTheActiveDays(unit, t),
        last_cleaning_date: lastCleaning ? moment(lastCleaning).format("YYYY-MM-DD") : null,
        first_cleaning_date: firstCleaning ? moment(firstCleaning).format("YYYY-MM-DD") : null,
      };
    });

    const selectedPartnerIds = getSelectedPartnerIds();

    return {
      partner_id: selectedUnits?.[0]?.partner?.id,
      new_partner_ids: selectedPartnerIds,
      building_units,
      subject: values?.subject?.trim(),
      manager_employee_id: values?.manager_employee_id || null,
      productType: productType,
      displayPrice: displayPrice,
      language: selectedLanguage,
      locations: selectedLocations,
      hideStreetNumber: hideStreetNumber,
      preserveSelectedUnitsOnly: true,
      partnerStates: selectedPartnerStates,
      additionalPDF: additionalPDF,
    };
  };

    const handleShowSummary = async () => {
    const selectedPartnerIds = getSelectedPartnerIds();

    if (selectedPartnerIds.length === 0) {
      notification.warning({ message: t("partners.validation.selectPartner") });
      return;
    }

    try {
      setIsSubmitting(true);
      
      const values = await offerVacantForm.validateFields();
      const payload = buildPayload(values);

      const { data } = await coreApi.post("/partners/offer-vacant-houses-email/preview", payload);
      setEmailPreview(data?.html || t("emails.mailError"));
      setIsButtonDisabled(false);
    } catch (error) {
      if (!error?.errorFields) {
        handleApiError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 99) {
          clearInterval(interval);
          return 99;
        }
        return p + 1;
      });
    }, 100);

    try {
      setIsSubmitting(true);
      
      const values = await offerVacantForm.validateFields();
      const payload = buildPayload(values);

      await coreApi.post("/partners/offer-vacant-houses-email/send", payload);
      notification.success({ message: t("emails.mailSent") });
      handleClose();
    } catch (error) {
      if (!error?.errorFields) {
        handleApiError(error);
      }
    } finally {
      setIsSubmitting(false);
      clearInterval(interval);
      setProgress(100);
    }
  };

  const handleClose = () => {
    setEmailPreview("");
    setIsButtonDisabled(true);
    setProductType("automatic");
    setDisplayPrice("with_substitute_reward");
    setSelectedLanguage("automatic");
    setSelectedLocations([]);
    setHideStreetNumber(true);
    setAdditionalPDF("downloadPdf");
    setSelectedPartnerStates([]);
    resetFiltering();
    offerVacantForm.resetFields();
    dispatch(clearAvailablePartnerStates());
    onCancel();
  };

  return (
    <Modal
      open={isVisible}
      onCancel={handleClose}
      width={1000}
      title={
        <>
          <strong>{t("schedules.informPartnerOptions.offerVacantHousesForSubstitute")}. {" "}</strong>
          {t("partners.previewInformEmail")} {" - "} <strong>{t("schedules.partner")}:</strong>{" "}
          {selectedUnits?.[0]?.partner ? (
            <PartnerLink partner={selectedUnits[0].partner} />
          ) : (
            t("partners.noContractor")
          )}
        </>
      }
      footer={[
        <Button
          key="confirm"
          type="primary"
          icon={
            isSubmitting ? (
              <Progress
                type="circle"
                percent={progress}
                width={24}
                strokeWidth={10}
                showInfo={true}
                style={{ marginRight: 8 }}
                status="active"
              />
            ) : (
              <SendOutlined />
            )
          }
          disabled={isButtonDisabled || isSubmitting}
          onClick={handleSubmit}
        >
          {t("common.confirm")}
        </Button>
      ]}
    >
      <Form form={offerVacantForm} layout="vertical">
        <PdfSettingsForm
          key="offer-vacant-houses-pdf-settings"
          productType={productType}
          setProductType={setProductType}
          rewardVisibility={displayPrice}
          setRewardVisibility={setDisplayPrice}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          showAutomaticLanguage={true}
          showAutomaticProductType={true}
          customRewardVisibilityOptions={["with_substitute_reward"]}
          hideStreetNumber={hideStreetNumber}
          setHideStreetNumber={setHideStreetNumber}
          showStreetNumberSection={true}
          additionalPDF={additionalPDF}
          setAdditionalPDF={setAdditionalPDF}
          showAdditionalPDFSection={true}
        />
        <Form.Item
          label={t("emails.subject")}
          name="subject"
          rules={[{ required: true, whitespace: true, message: t("common.validation.required") }]}
          style={{ marginTop: 16 }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={t("emails.managerNameOverride")}
          name="manager_employee_id"
        >
          <ManagerSelect
            withFormItem={false}
            clearable
            showSearch
            activeOnly
            placeholder={t("emails.managerNameOverride")}
          />
        </Form.Item>
        <Divider />

        <Row gutter={12}>
          <Col span={24}>
            <AvailableLocationsSelect
              label={t("schedules.locationFiltering")}
              name="locations"
              mode="multiple"
              onChange={(locations) => {
                dispatch(clearAvailablePartnerStates());
                setSelectedPartnerStates([]);
                
                offerVacantForm.setFieldValue("state", undefined);
                
                if (Array.isArray(locations)) {
                  setSelectedLocations(locations.map(loc => loc.id));
                } else {
                  setSelectedLocations(locations ? [locations.id] : []);
                }
              }}
            />
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={24}>
            <PartnerStateSelector
              hideLabel={false}
              clearable={true}
              mode="multiple"
              autoPopulate={true}
              form={offerVacantForm}
              onAutoPopulate={setSelectedPartnerStates}
              sendOnchange={setSelectedPartnerStates}
            />
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={24}>
            <AllPartnersForOrdersSelect
              label={t("schedules.partnerFiltering")}
              name="partners"
              mode="multiple"
              required={true}
              selectedLocations={selectedLocations}
              selectedPartnerStates={selectedPartnerStates}
              onPartnersLoaded={handlePartnersLoadedWithForm}
              onChange={(partners) => {
                if (Array.isArray(partners)) {
                  handlePartnerSelectionChange(partners);
                } else {
                  handlePartnerSelectionChange(partners ? [partners] : []);
                }
              }}
              value={selectedPartners}
            />
          </Col>
        </Row>

        {filteredPartners.length > 0 && (
          <>
            <Divider />
            <Row gutter={12}>
              <Col span={24}>
                <PartnerFilterTable
                  partners={filteredPartners}
                  checkedPartners={checkedPartners}
                  onPartnerCheck={handlePartnerCheck}
                />
              </Col>
            </Row>
          </>
        )}

        <Row style={{ marginTop: 16 }}>
          <Col>
            <Button
              type="primary"
              onClick={selectAll}
              disabled={isSubmitting || filteredPartners.length === 0}
              style={{marginRight: '20px'}}
            >
              {t("common.selectAll")}
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={deselectAll}
              disabled={isSubmitting || filteredPartners.length === 0}
            >
              {t("common.deselectAll")}
            </Button>
          </Col>
          <Col style={{marginLeft: 'auto'}}>
            <Button type="primary" onClick={handleShowSummary} icon={<EyeOutlined />} disabled={isSubmitting}>
              {t("common.continue")}
            </Button>
          </Col>
        </Row>

        <Divider />

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label={t("schedules.bulkTimeRangeSetting")}>
              <DatePicker.RangePicker
                onChange={(dates) => {
                  if (!dates || dates.length !== 2) return;
                  selectedUnits.forEach(unit => {
                    offerVacantForm.setFieldValue(["cleaning_range", unit.id], dates);
                  });
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12} style={{ fontWeight: "bold" }}>
          <Col span={6}>{t("schedules.addressEntrance")}</Col>
          <Col span={5}>{t("schedules.cleaningDay")}</Col>
          <Col span={9}>{t("schedules.newSubstituteStartsCleaningWeekFromTo")}</Col>
        </Row>

        {selectedUnits && selectedUnits.map((unit) => (
          <Row gutter={12} key={unit.id}>
            <Col span={6}>
              <Input disabled value={`${unit.building_unit?.street || ""} ${unit.building_unit?.house_number || ""}`} />
            </Col>
            <Col span={5}>
              <Input disabled value={getTheActiveDays(unit, t, true) || t(`rrules.byweekday.${unit.day || "-"}`)} />
            </Col>
            <Col span={9}>
              <Form.Item
                name={["cleaning_range", unit.id]}
                initialValue={[moment(unit.action_week_from), moment(unit.action_week_to)]}
                rules={[{ required: true, message: t("schedules.validation.cleaningRange") }]}
              >
                <DatePicker.RangePicker />
              </Form.Item>
            </Col>
          </Row>
        ))}

        {emailPreview && (
          <Card style={{ marginTop: 24 }}>
            <iframe
              title="Email Preview"
              style={{ width: '100%', height: '500px', border: '1px solid #ccc' }}
              srcDoc={emailPreview}
            />
          </Card>
        )}
      </Form>
    </Modal>
  );
};

export default OfferVacantHousesModal;
